"use client"
import type {} from "@mui/x-data-grid/themeAugmentation"
import { Refine } from "@refinedev/core"
import { RefineSnackbarProvider, useNotificationProvider } from "@refinedev/mui"
import routerProvider from "@refinedev/nextjs-router"
import dataProvider from "@refinedev/simple-rest"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { SessionProvider } from "next-auth/react"
import { authProvider } from "@/lib/auth-provider"
import { appResources, settingsResource } from "@/lib/app-resources"
import {
  UI_ACTION_COLORS,
  UI_BACKGROUND_COLORS,
  UI_BORDER_COLORS,
  UI_BRAND_COLORS,
  UI_FEEDBACK_COLORS,
  UI_TEXT_COLORS,
} from "@/components/ui/color-tokens"

const API_URL = "/api"

const zhTranslations: Record<string, string> = {
  "notifications.success": "操作成功",
  "notifications.error": "操作失败",
  "notifications.undoable": "{{seconds}} 秒内可撤销",
  "notifications.createSuccess": "{{resource}}新建成功",
  "notifications.createError": "新建{{resource}}失败（{{statusCode}}）",
  "notifications.deleteSuccess": "{{resource}}已删除",
  "notifications.deleteError": "删除{{resource}}失败（{{statusCode}}）",
  "notifications.editSuccess": "{{resource}}已保存",
  "notifications.editError": "保存{{resource}}失败（{{statusCode}}）",
  "users.users": "用户",
  "departments.departments": "部门",
  "inquiries.inquiries": "询单",
  "orders.orders": "订单",
  "ops-accounts.ops-accounts": "账号数据",
  "finance.finance": "财务",
  "suppliers.suppliers": "供应商",
  "files.files": "文件",
  "playbooks.playbooks": "定制师手册",
  "insights.insights": "认知库",
  "guides.guides": "使用指南",
  "buttons.create": "新建",
  "buttons.save": "保存",
  "buttons.delete": "删除",
  "buttons.edit": "编辑",
  "buttons.cancel": "取消",
  "buttons.confirm": "确认",
  "buttons.refresh": "刷新",
  "buttons.show": "查看",
  "buttons.clone": "复制",
  "warnWhenUnsavedChanges": "有未保存的修改，确定离开？",
  "deleteConfirm.title": "确认删除？",
  "deleteConfirm.content": "此操作不可撤销",
  "deleteConfirm.buttons.confirm": "确认删除",
  "deleteConfirm.buttons.cancel": "取消",
}

const i18nProvider = {
  translate: (key: string, params?: Record<string, unknown> | string, defaultValue?: string) => {
    if (typeof params === "string") {
      return zhTranslations[key] ?? params
    }
    const template = zhTranslations[key] ?? defaultValue ?? key
    if (!params) return template
    return template.replace(/\{\{(\w+)\}\}/g, (_, k) => String((params as Record<string, unknown>)[k] ?? ""))
  },
  getLocale: () => "zh",
  changeLocale: async () => {},
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: UI_BRAND_COLORS.primary, light: UI_BRAND_COLORS.primaryLight, dark: UI_BRAND_COLORS.primaryDark, contrastText: UI_TEXT_COLORS.inverse },
    secondary: { main: UI_BRAND_COLORS.secondary },
    background: { default: UI_BACKGROUND_COLORS.default, paper: UI_BACKGROUND_COLORS.paper },
    text: { primary: UI_TEXT_COLORS.primary, secondary: UI_TEXT_COLORS.secondary },
    divider: UI_BORDER_COLORS.subtle,
    info: { main: UI_FEEDBACK_COLORS.info, contrastText: UI_TEXT_COLORS.inverse },
    success: { main: UI_FEEDBACK_COLORS.success, contrastText: UI_TEXT_COLORS.inverse },
    warning: { main: UI_FEEDBACK_COLORS.warning, contrastText: UI_TEXT_COLORS.inverse },
    error: { main: UI_FEEDBACK_COLORS.error, contrastText: UI_TEXT_COLORS.inverse },
    action: {
      hover: UI_ACTION_COLORS.hover,
      selected: UI_ACTION_COLORS.selectedPrimary,
      disabledBackground: UI_ACTION_COLORS.disabledBackground,
    },
  },
  shape: { borderRadius: 12 },
  shadows: [
    "none","none","none","none","none","none","none","none",
    "none","none","none","none","none","none","none","none",
    "none","none","none","none","none","none","none","none","none",
  ],
  typography: {
    fontFamily: "'Inter', 'PingFang SC', 'Noto Sans SC', sans-serif",
    fontSize: 14,
    h1: { fontSize: 52, fontWeight: 700, letterSpacing: -2.2 },
    h2: { fontSize: 40, fontWeight: 700, letterSpacing: -1.6 },
    h3: { fontSize: 32, fontWeight: 700, letterSpacing: -1.1 },
    h4: { fontSize: 26, fontWeight: 700, letterSpacing: -0.8 },
    h5: { fontSize: 20, fontWeight: 700, letterSpacing: -0.4 },
    h6: { fontSize: 16, fontWeight: 700, letterSpacing: -0.3 },
    body1: { fontSize: 14 },
    body2: { fontSize: 12 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        * { box-sizing: border-box; }
        html, body, #__next { min-height: 100%; }
        body {
          background:
            radial-gradient(circle at top right, rgba(79,126,255,0.12), transparent 28%),
            linear-gradient(180deg, #f6f8fc 0%, #f3f5f9 100%);
        }
        @keyframes loginAmbientFlow {
          0% {
            transform: translate3d(-3%, -2%, 0) scale(1);
            opacity: 0.82;
          }
          50% {
            transform: translate3d(2%, 2%, 0) scale(1.08);
            opacity: 1;
          }
          100% {
            transform: translate3d(4%, -2%, 0) scale(1.12);
            opacity: 0.88;
          }
        }
        @keyframes loginAmbientSweep {
          0% {
            transform: translate3d(-8%, 0, 0) rotate(0.5deg);
            opacity: 0.35;
          }
          50% {
            transform: translate3d(2%, -2%, 0) rotate(1deg);
            opacity: 0.72;
          }
          100% {
            transform: translate3d(10%, 1%, 0) rotate(1.5deg);
            opacity: 0.4;
          }
        }
        @keyframes appAvatarGlow {
          0% { opacity: 0.85; transform: scale(0.94); }
          60% { opacity: 0.25; transform: scale(1.08); }
          100% { opacity: 0; transform: scale(1.12); }
        }
        @keyframes appAvatarSweep {
          0%, 3% { opacity: 0; transform: translateX(-120%); }
          7% { opacity: 0.4; }
          22% { opacity: 0.4; transform: translateX(120%); }
          23%, 100% { opacity: 0; transform: translateX(120%); }
        }
        * { scrollbar-width: thin; scrollbar-color: rgba(15,23,42,0.14) transparent; }

        @media print {
          /* 强制输出背景色/配色(默认浏览器打印不印背景,黄单据会变白)→ 保持页面渲染色 */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          html, body, #__next {
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          /* 需求确认单打印:整页画布底色 = 黄复写纸。画布底色取自 html(根元素)→ 必须改 html 背景才能跨页铺满,
             改 body 不传播(html 已占 #fff)。scoped 到 html class,不影响其它打印 */
          html.ops-confirm-print, html.ops-confirm-print body, html.ops-confirm-print #__next { background: #fffdf2 !important; }
          [data-print-sibling-hide] { display: none !important; }
          .ops-print-area {
            border: none !important;
            padding: 0 !important;
            margin: 0 !important; /* 满铺整页(单据自带内边距,内容不贴边)→ 单一底色,不再"白页+黄单"双色;手机看也大 */
            width: auto !important; /* 覆盖屏幕态 width:100%/maxWidth → 打印时收进页内,不溢出右截 */
            max-width: 100% !important;
            box-sizing: border-box !important;
            box-shadow: none !important;
          }
          .ops-print-hidden { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }
        /* margin:0 → 浏览器不印页眉/页脚(标题/日期/网址)。满页黄底 = html 背景(scoped)+ 单据 minHeight 铺满,
           单据自带内边距,内容不贴纸边。⚠️ 不要改成有边距:一旦 margin>0,浏览器会在边距印回标题、边距区也会露白底(确稿回退) */
        @page { size: A4; margin: 0; }
      `,
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(15,23,42,0.08)",
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(15,23,42,0.08)",
          borderRadius: 18,
          boxShadow: "0 10px 32px rgba(15,23,42,0.05)",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: "20px 20px 0 20px" },
        title: { fontSize: 15, fontWeight: 700 },
        subheader: { fontSize: 12, color: "rgba(15,23,42,0.46)" },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, "&:last-child": { paddingBottom: 20 } },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          borderRadius: 12,
          minHeight: 38,
          letterSpacing: -0.1,
        },
        containedPrimary: {
          background: "#2563eb",
          "&:hover": { background: "#1d4ed8" },
        },
        outlined: {
          borderColor: "rgba(37,99,235,0.18)",
          background: "rgba(239,246,255,0.78)",
          color: "#1d4ed8",
          "&:hover": {
            borderColor: "rgba(37,99,235,0.28)",
            background: "rgba(219,234,254,0.92)",
          },
          "&.Mui-disabled": {
            borderColor: "rgba(37,99,235,0.12)",
            background: "rgba(239,246,255,0.38)",
            color: "rgba(37,99,235,0.38)",
          },
        },
        text: {
          color: "rgba(15,23,42,0.78)",
          "&:hover": { background: "rgba(15,23,42,0.04)" },
        },
        sizeSmall: { fontSize: 12, padding: "4px 10px", minHeight: 30, borderRadius: 10 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, "&:hover": { background: "rgba(15,23,42,0.04)" } },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            fontSize: 13,
            background: "rgba(248,250,252,0.88)",
            "& fieldset": { borderColor: "rgba(15,23,42,0.12)" },
            "&:hover fieldset": { borderColor: "rgba(15,23,42,0.32)" },
            "&.Mui-focused fieldset": { borderColor: "rgba(15,23,42,0.45)", borderWidth: 1 },
          },
          "& .MuiInputLabel-root": { fontSize: 13, color: "rgba(15,23,42,0.44)" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontSize: 11, fontWeight: 600, height: 24, borderRadius: 999 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: 11, background: "#0f172a", color: "#fff", borderRadius: 6 },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "rgba(15,23,42,0.08)" } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginBottom: 6,
          minHeight: 42,
          borderRadius: 14,
          // 未选中也留一条透明 1px 边占位 —— 选中态只换边色,内容区尺寸不变,杜绝"点击位移 1px"
          border: "1px solid transparent",
          "&.Mui-selected": {
            background: "linear-gradient(90deg, rgba(37,99,235,0.12) 0%, rgba(37,99,235,0.05) 100%)",
            border: "1px solid rgba(37,99,235,0.14)",
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 13,
          fontWeight: 500,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: 12, color: "rgba(15,23,42,0.48)", borderTop: "1px solid rgba(15,23,42,0.08)" },
        select: { fontSize: 12 },
      },
    },
    MuiDataGrid: {
      defaultProps: { density: "compact" },
      styleOverrides: {
        root: {
          border: "none",
          fontSize: 13,
          "--DataGrid-rowBorderColor": "rgba(15,23,42,0.05)",
          "& .MuiDataGrid-columnHeaders":         { background: "rgba(15,23,42,0.02)", borderBottom: "1px solid rgba(15,23,42,0.08)", minHeight: "40px !important", maxHeight: "40px !important" },
          "& .MuiDataGrid-columnHeaderTitle":      { fontSize: 11, fontWeight: 600, color: "rgba(15,23,42,0.42)", textTransform: "uppercase", letterSpacing: 0.6 },
          "& .MuiDataGrid-columnSeparator":        { display: "none" },
          "& .MuiDataGrid-row":                    { borderBottom: "1px solid rgba(15,23,42,0.05)" },
          "& .MuiDataGrid-row:hover":              { background: "rgba(37,99,235,0.04)" },
          "& .MuiDataGrid-cell":                   { border: "none", display: "flex", alignItems: "center" },
          "& .MuiDataGrid-cell:focus":             { outline: "none" },
          "& .MuiDataGrid-cell:focus-within":      { outline: "none" },
          "& .MuiDataGrid-columnHeader:focus":     { outline: "none" },
          "& .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
          "& .MuiDataGrid-footerContainer":        { borderTop: "1px solid rgba(15,23,42,0.08)", minHeight: 44 },
          "& .MuiDataGrid-virtualScroller":        { overflowX: "hidden" },
          "& .MuiCheckbox-root":                   { color: "rgba(15,23,42,0.26)" },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: "rgba(248,250,252,0.92)",
          border: "none",
          borderRight: "1px solid rgba(15,23,42,0.08)",
          backdropFilter: "blur(12px)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "rgba(245,247,251,0.82)",
          borderBottom: "none",
          boxShadow: "0 1px 0 rgba(15,23,42,0.04)",
          backdropFilter: "blur(14px)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { fontSize: 13, borderRadius: 8, border: "1px solid" },
      },
    },
    MuiPopover: {
      defaultProps: { disableScrollLock: true },
    },
    MuiMenu: {
      defaultProps: { disableScrollLock: true },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: 13,
          fontWeight: 600,
          minHeight: 42,
          "&.Mui-selected": {
            color: "#2563eb",
          },
          "&:hover": {
            background: "rgba(37,99,235,0.08)",
            color: "#2563eb",
          },
          "& .MuiTouchRipple-child": {
            backgroundColor: "#2563eb",
          },
        },
      },
    },
  },
})

function RefineApp({ children }: { children: React.ReactNode }) {
  const notificationProvider = useNotificationProvider()
  return (
    <Refine
      routerProvider={routerProvider}
      authProvider={authProvider}
      dataProvider={dataProvider(API_URL)}
      notificationProvider={notificationProvider}
      i18nProvider={i18nProvider}
      resources={[...appResources, settingsResource]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        title: {
          text: "Along",
        },
      }}
    >
      {children}
    </Refine>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RefineSnackbarProvider>
          <RefineApp>{children}</RefineApp>
        </RefineSnackbarProvider>
      </ThemeProvider>
    </SessionProvider>
  )
}
